import React, { Component } from 'react';
import { observable, action, autorun } from 'mobx';
import { observer, inject } from 'mobx-react';
import { toast } from 'react-toastify';
import {
  Modal, ModalHeader, ModalBody,
  FormInput, Button, Row, Col
} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import moment from 'moment';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const TYPES = [
  { key: 'noPaied', label: '미결제 알림', icon: 'alert-circle' },
  { key: 'noPeriod', label: '기간 만료 알림', icon: 'clock' },
  { key: 'event', label: '이벤트 알림', icon: 'gift' },
  { key: 'planUpgrade', label: '업그레이드 알림', icon: 'arrow-up-circle' },
  { key: 'planDowngrade', label: '다운그레이드 알림', icon: 'arrow-down-circle' },
];

const PLACEHOLDERS = [
  { key: 'storeName', desc: '스토어명' },
  { key: 'name', desc: '담당자명' },
  { key: 'expiryDate', desc: '만료일' },
  { key: 'orderDate', desc: '결제일' },
  { key: 'newPlan', desc: '새 플랜명' },
  { key: 'prevPlan', desc: '이전 플랜명' },
  { key: 'eventTitle', desc: '이벤트 제목' },
];

@inject(({ AOA }) => ({
  getNotificationTypeTemplates: AOA.getNotificationTypeTemplates,
  saveNotificationTypeTemplate: AOA.saveNotificationTypeTemplate,
  resetNotificationTypeTemplate: AOA.resetNotificationTypeTemplate,
  previewNotificationTypeTemplate: AOA.previewNotificationTypeTemplate,
  getDefaultMailHtml: AOA.getDefaultMailHtml,
}))
@observer
class MailTemplateModal extends Component {

  @observable activeType = 'noPaied';
  @observable items = [];
  @observable isLoading = false;
  @observable isSaving = false;
  @observable drafts = {};        // {type: {emailSubject, emailHtml}}
  @observable preview = { html: '', subject: '' };
  @observable isPreviewLoading = false;
  // 편집 모드 — 'html' (raw 소스, 모든 스타일 보존) | 'visual' (Quill 리치 에디터, 단순 편집용)
  @observable editorMode = 'html';
  _previewDebounce = null;

  quillRef = React.createRef();
  quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['blockquote', 'link', 'image'],
      ['clean']
    ],
    clipboard: { matchVisual: false }
  };
  quillFormats = ['header','bold','italic','underline','strike','color','background','align','list','bullet','blockquote','link','image'];

  PERSIST_KEY = 'mailTemplate_drafts';

  componentDidMount() {
    // 탭 전환/리마운트 시 편집 중인 drafts 복원
    try {
      const saved = window.sessionStorage && window.sessionStorage.getItem(this.PERSIST_KEY);
      if (saved && this.props.isOpen) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.drafts) {
          this.drafts = parsed.drafts;
          if (parsed.activeType) this.activeType = parsed.activeType;
          if (parsed.editorMode) this.editorMode = parsed.editorMode;
        }
      }
    } catch (e) {}
    // drafts 변경마다 자동 저장 (모달 열렸을 때만)
    this._persistDisposer = autorun(() => {
      try {
        if (this.props.isOpen && Object.keys(this.drafts || {}).length > 0) {
          window.sessionStorage && window.sessionStorage.setItem(this.PERSIST_KEY, JSON.stringify({
            drafts: this.drafts,
            activeType: this.activeType,
            editorMode: this.editorMode,
          }));
        }
      } catch (e) {}
    });
  }

  componentWillUnmount() {
    if (this._persistDisposer) { try { this._persistDisposer(); } catch (e) {} }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.load();
    }
    // 모달이 닫히면 sessionStorage 정리
    if (!this.props.isOpen && prevProps.isOpen) {
      try { window.sessionStorage && window.sessionStorage.removeItem(this.PERSIST_KEY); } catch (e) {}
    }
  }

  @action load = async () => {
    this.isLoading = true;
    try {
      const res = await this.props.getNotificationTypeTemplates();
      if (res && res.data && res.data.error) {
        toast.error(res.data.error);
      } else if (res && res.data && res.data.items) {
        this.items = res.data.items;
        const drafts = {};
        res.data.items.forEach(it => {
          drafts[it.type] = {
            emailSubject: it.emailSubject || '',
            emailHtml: it.emailHtml || '',  // 비어있으면 ensureDefaultLoaded()가 채움
          };
        });
        this.drafts = drafts;
        // 현재 활성 타입의 기본 HTML도 자동으로 로드 (에디터에 즉시 표시)
        await this.ensureDefaultLoaded(this.activeType);
        this.refreshPreview();
      }
    } catch (e) {
      toast.error('템플릿 조회 실패');
    } finally {
      this.isLoading = false;
    }
  };

  // 저장된 커스텀이 없는 타입은 기본 메일 HTML을 lazy load해서 에디터에 표시
  @action ensureDefaultLoaded = async (type) => {
    const item = this.items.find(i => i.type === type);
    if (!item) return;
    // 이미 커스텀 또는 default 캐시가 있으면 skip
    if (item.emailHtml) return;
    if (item.defaultHtml) {
      // 캐시가 있는데 draft가 비어있으면 채워줌
      if (this.drafts[type] && !this.drafts[type].emailHtml) {
        this.drafts[type].emailHtml = item.defaultHtml;
      }
      return;
    }
    try {
      const res = await this.props.getDefaultMailHtml({ type });
      if (res && res.data && res.data.html) {
        item.defaultHtml = res.data.html;
        if (this.drafts[type] && !this.drafts[type].emailHtml) {
          this.drafts[type].emailHtml = res.data.html;
        }
      }
    } catch (e) { /* ignore */ }
  };

  @action setType = async (key) => {
    this.activeType = key;
    await this.ensureDefaultLoaded(key);
    this.refreshPreview();
  };

  @action onChange = (field, value) => {
    if (!this.drafts[this.activeType]) this.drafts[this.activeType] = {};
    this.drafts[this.activeType][field] = value;
    this.schedulePreview();
  };

  schedulePreview = () => {
    if (this._previewDebounce) clearTimeout(this._previewDebounce);
    this._previewDebounce = setTimeout(() => this.refreshPreview(), 800);
  };

  @action refreshPreview = async () => {
    const draft = this.drafts[this.activeType];
    if (!draft) return;
    this.isPreviewLoading = true;
    try {
      const res = await this.props.previewNotificationTypeTemplate({
        type: this.activeType,
        emailSubject: draft.emailSubject || '',
        emailHtml: draft.emailHtml || '',
      });
      if (res && res.data && !res.data.error) {
        this.preview = { html: res.data.html || '', subject: res.data.subject || '' };
      }
    } catch (e) { /* ignore */ }
    finally { this.isPreviewLoading = false; }
  };

  isDirty(type) {
    const item = this.items.find(i => i.type === type);
    const draft = this.drafts[type];
    if (!item || !draft) return false;
    // 본문 baseline: 저장된 커스텀이 있으면 그걸로, 없으면 로드된 기본 HTML
    const htmlBaseline = item.emailHtml || item.defaultHtml || '';
    return draft.emailSubject !== (item.emailSubject || '')
      || draft.emailHtml !== htmlBaseline;
  }

  @action save = async () => {
    const draft = this.drafts[this.activeType];
    if (!draft) return;
    this.isSaving = true;
    try {
      const res = await this.props.saveNotificationTypeTemplate({
        type: this.activeType,
        emailSubject: draft.emailSubject || '',
        emailHtml: draft.emailHtml || '',
      });
      if (res && res.data && res.data.error) toast.error(res.data.error);
      else { toast.success('메일 템플릿이 저장되었습니다.'); await this.load(); }
    } catch (e) { toast.error('저장 실패'); }
    finally { this.isSaving = false; }
  };

  @action loadDefaultHtml = async () => {
    if (!window.confirm('현재 편집 중인 내용이 기본 메일 양식으로 대체됩니다. 계속하시겠습니까?')) return;
    this.isLoading = true;
    try {
      const res = await this.props.getDefaultMailHtml({ type: this.activeType });
      if (res && res.data && !res.data.error && res.data.html) {
        // items의 defaultHtml 캐시도 갱신
        const item = this.items.find(i => i.type === this.activeType);
        if (item) item.defaultHtml = res.data.html;
        this.onChange('emailHtml', res.data.html);
        toast.success('기본 양식을 다시 불러왔습니다.');
      } else {
        toast.error((res && res.data && res.data.error) || '기본 양식 로드 실패');
      }
    } catch (e) { toast.error('기본 양식 로드 실패'); }
    finally { this.isLoading = false; }
  };

  insertPlaceholderToSubject = (ph) => {
    const d = this.drafts[this.activeType] || {};
    const cur = d.emailSubject || '';
    this.onChange('emailSubject', cur + `{${ph}}`);
  };

  insertPlaceholderToEditor = (ph) => {
    if (this.editorMode === 'visual') {
      const editor = this.quillRef.current && this.quillRef.current.getEditor();
      if (editor) {
        const range = editor.getSelection(true);
        const insertAt = range ? range.index : editor.getLength();
        editor.insertText(insertAt, `{${ph}}`);
        editor.setSelection(insertAt + (`{${ph}}`).length);
      }
    } else {
      // HTML 소스 모드: 끝에 추가
      const d = this.drafts[this.activeType] || {};
      const cur = d.emailHtml || '';
      this.onChange('emailHtml', cur + `{${ph}}`);
    }
  };

  close = () => {
    if (this.isSaving) return;
    if (this.props.onClose) this.props.onClose();
  };

  render() {
    const { isOpen } = this.props;
    if (!isOpen) return null;

    const item = this.items.find(i => i.type === this.activeType) || {};
    const draft = this.drafts[this.activeType] || { emailSubject: '', emailHtml: '' };
    const dirty = this.isDirty(this.activeType);

    return (
      <>
        <style>{`
          .modal-dialog.mail-tpl-modal { max-width: 1500px !important; width: 96vw; }
          .mail-tpl-modal .modal-body { max-height: 88vh; overflow-y: auto; }
          .mail-tpl-modal .ql-editor { min-height: 480px; max-height: 600px; font-size: 14px; }
        `}</style>
        <Modal size="lg" className="mail-tpl-modal" open={isOpen} toggle={() => {}} centered>
          <ModalHeader>
            <FeatherIcon icon="mail" size="16" style={{verticalAlign: '-3px', marginRight: 6}}/>
            메일 템플릿 관리
          </ModalHeader>
          <ModalBody>
            <p style={{fontSize: 12, color: '#6B7280', marginBottom: 12}}>
              알림 발송 시 사용되는 이메일 제목과 본문 HTML을 타입별로 커스터마이징합니다. 본문은 리치 에디터에서 자유롭게 편집할 수 있고,
              저장하면 기본 메일 빌더 대신 이 본문이 그대로 발송됩니다 (헤더/푸터는 자동 부착).
            </p>

            <div style={{display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', marginBottom: 14}}>
              {TYPES.map(t => {
                const active = this.activeType === t.key;
                const itemForT = this.items.find(i => i.type === t.key);
                const tDirty = this.isDirty(t.key);
                const hasHtml = itemForT && itemForT.emailHtml;
                return (
                  <button key={t.key} type="button" onClick={() => this.setType(t.key)}
                    style={{
                      padding: '10px 14px', fontSize: 13, fontWeight: active ? 700 : 500,
                      color: active ? '#2563EB' : '#6B7280',
                      background: active ? '#EFF6FF' : 'transparent', border: 'none',
                      borderBottom: active ? '2px solid #2563EB' : '2px solid transparent',
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}>
                    <FeatherIcon icon={t.icon} size="13"/>
                    {t.label}
                    {hasHtml && <span style={{fontSize: 10, color: '#10B981', marginLeft: 4}}>●</span>}
                    {tDirty && <span style={{fontSize: 10, color: '#D97706', marginLeft: 2}}>*</span>}
                  </button>
                );
              })}
            </div>

            {this.isLoading ? (
              <div className="text-center p-4">로딩 중…</div>
            ) : (
              <>
                <div style={{padding: '8px 12px', background: '#F9FAFB', borderRadius: 6, marginBottom: 10, fontSize: 12, color: '#6B7280'}}>
                  {item.emailHtml
                    ? <>커스텀 양식 저장됨 · 마지막 수정: {item.updatedAt ? moment(item.updatedAt).format('YYYY-MM-DD HH:mm') : '-'} ({item.updatedBy || '-'})</>
                    : <>현재 발송되는 기본 메일 양식을 에디터에 불러왔습니다. 수정 후 저장하면 다음 발송부터 이 양식으로 적용됩니다.</>}
                </div>

                <div style={{padding: '8px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, marginBottom: 12, fontSize: 12}}>
                  <strong>변수:</strong>{' '}
                  {PLACEHOLDERS.map(ph => (
                    <code key={ph.key}
                      onClick={() => this.insertPlaceholderToEditor(ph.key)}
                      title={`에디터에 {${ph.key}} 삽입`}
                      style={{background: '#FEF3C7', padding: '1px 6px', borderRadius: 3, marginRight: 6, fontFamily: 'ui-monospace, monospace', cursor: 'pointer'}}>
                      {`{${ph.key}}`} = {ph.desc}
                    </code>
                  ))}
                  <span style={{marginLeft: 6, color: '#6B7280'}}>(클릭하면 에디터에 삽입)</span>
                </div>

                <Row>
                  <Col md="7">
                    <div className="mb-2">
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4}}>
                        <label style={{fontSize: 12, fontWeight: 600, margin: 0}}>이메일 제목
                          <span style={{marginLeft: 8, fontSize: 11, color: '#6B7280'}}>기본값: {item.defaultSubject}</span>
                        </label>
                        <div style={{display: 'flex', gap: 4}}>
                          {PLACEHOLDERS.slice(0, 5).map(ph => (
                            <button key={ph.key} type="button"
                              onClick={() => this.insertPlaceholderToSubject(ph.key)}
                              style={{fontSize: 10, padding: '2px 6px', border: '1px solid #D1D5DB', borderRadius: 3, background: '#fff', cursor: 'pointer'}}>
                              +{`{${ph.key}}`}
                            </button>
                          ))}
                        </div>
                      </div>
                      <FormInput value={draft.emailSubject}
                        onChange={e => this.onChange('emailSubject', e.target.value)}
                        placeholder={item.defaultSubject}/>
                    </div>

                    <div className="mb-2" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <label style={{fontSize: 12, fontWeight: 600, margin: 0}}>이메일 본문</label>
                      <div style={{display: 'flex', gap: 6, alignItems: 'center'}}>
                        <div style={{display: 'inline-flex', border: '1px solid #D1D5DB', borderRadius: 4, overflow: 'hidden'}}>
                          <button type="button"
                            onClick={() => { this.editorMode = 'html'; }}
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              background: this.editorMode === 'html' ? '#EFF6FF' : '#fff',
                              color: this.editorMode === 'html' ? '#2563EB' : '#6B7280',
                              border: 'none', cursor: 'pointer',
                            }}>
                            <FeatherIcon icon="code" size="11" style={{verticalAlign: '-2px', marginRight: 3}}/>
                            HTML 소스
                          </button>
                          <button type="button"
                            onClick={() => {
                              if (window.confirm('비주얼 에디터로 전환하면 일부 인라인 스타일이나 table 구조가 손실될 수 있습니다. 계속하시겠습니까?')) {
                                this.editorMode = 'visual';
                              }
                            }}
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              background: this.editorMode === 'visual' ? '#EFF6FF' : '#fff',
                              color: this.editorMode === 'visual' ? '#2563EB' : '#6B7280',
                              border: 'none', borderLeft: '1px solid #D1D5DB', cursor: 'pointer',
                            }}>
                            <FeatherIcon icon="edit-3" size="11" style={{verticalAlign: '-2px', marginRight: 3}}/>
                            비주얼
                          </button>
                        </div>
                        <Button size="sm" theme="light" onClick={() => this.loadDefaultHtml()} disabled={this.isLoading}>
                          <FeatherIcon icon="rotate-ccw" size="11" style={{verticalAlign: '-2px', marginRight: 4}}/>
                          기본 양식으로 되돌리기
                        </Button>
                      </div>
                    </div>
                    {this.editorMode === 'html' ? (
                      <textarea
                        value={draft.emailHtml || ''}
                        onChange={e => this.onChange('emailHtml', e.target.value)}
                        spellCheck={false}
                        style={{
                          width: '100%',
                          minHeight: 540,
                          maxHeight: 700,
                          padding: 10,
                          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                          fontSize: 12,
                          lineHeight: 1.6,
                          border: '1px solid #D1D5DB',
                          borderRadius: 4,
                          background: '#FAFAFA',
                          resize: 'vertical',
                        }}
                      />
                    ) : (
                      <ReactQuill ref={this.quillRef}
                        value={draft.emailHtml || ''}
                        onChange={(value) => this.onChange('emailHtml', value)}
                        modules={this.quillModules}
                        formats={this.quillFormats}
                        theme="snow"/>
                    )}
                    <small style={{color: '#6B7280', fontSize: 11, marginTop: 4, display: 'block'}}>
                      {this.editorMode === 'html'
                        ? '※ HTML 소스 모드는 모든 인라인 스타일과 table 구조를 그대로 보존합니다. 발송 시 placeholder 치환 후 헤더/푸터가 감싸집니다.'
                        : '※ 비주얼 에디터는 단순 텍스트 편집에 적합합니다. 복잡한 인라인 스타일이나 table은 HTML 소스 모드에서 편집하세요.'}
                    </small>
                  </Col>

                  <Col md="5">
                    <div style={{position: 'sticky', top: 0}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
                        <label style={{fontSize: 12, fontWeight: 600, margin: 0}}>
                          <FeatherIcon icon="eye" size="12" style={{verticalAlign: '-2px', marginRight: 4}}/>
                          미리보기 (샘플 데이터)
                        </label>
                        <Button size="sm" theme="light" onClick={() => this.refreshPreview()} disabled={this.isPreviewLoading}>
                          <FeatherIcon icon="refresh-cw" size="11" style={{verticalAlign: '-2px', marginRight: 4}}/>
                          {this.isPreviewLoading ? '갱신 중…' : '갱신'}
                        </Button>
                      </div>
                      <div style={{padding: '6px 10px', background: '#F3F4F6', borderRadius: 4, marginBottom: 6, fontSize: 11, color: '#6B7280'}}>
                        제목: <strong style={{color: '#111827'}}>{this.preview.subject || '-'}</strong>
                      </div>
                      <div style={{border: '1px solid #E5E7EB', borderRadius: 4, padding: 4, background: '#FAFAFA'}}>
                        <iframe srcDoc={this.preview.html || '<div style="padding:40px;text-align:center;color:#9CA3AF">미리보기를 갱신해 주세요</div>'}
                          style={{width: '100%', height: 600, border: 0, background: '#fff', display: 'block'}}
                          title="mail-template-preview"/>
                      </div>
                      <small style={{display: 'block', marginTop: 6, color: '#9CA3AF', fontSize: 11}}>
                        샘플: storeName=샘플 스토어, name=홍길동, expiryDate=+7일, newPlan=프리미엄, prevPlan=베이직
                      </small>
                    </div>
                  </Col>
                </Row>

                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #E5E7EB', paddingTop: 12, marginTop: 16}}>
                  <div>
                    {item.emailHtml && (
                      <Button theme="light" size="sm" disabled={this.isSaving}
                        onClick={() => {
                          if (window.confirm('저장된 커스텀 HTML을 삭제하고 기본 메일 빌더로 되돌리시겠습니까?')) {
                            this.onChange('emailHtml', '');
                            this.save();
                          }
                        }}>
                        <FeatherIcon icon="rotate-ccw" size="12" style={{verticalAlign: '-2px', marginRight: 4}}/>
                        커스텀 HTML 삭제
                      </Button>
                    )}
                  </div>
                  <div style={{display: 'flex', gap: 8}}>
                    <Button theme="light" size="sm" onClick={() => this.close()} disabled={this.isSaving}>닫기</Button>
                    <Button theme="primary" size="sm" disabled={this.isSaving || !dirty} onClick={() => this.save()}>
                      <FeatherIcon icon="save" size="12" style={{verticalAlign: '-2px', marginRight: 4}}/>
                      {this.isSaving ? '저장 중…' : (dirty ? '저장' : '변경 없음')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </>
    );
  }
}

export default MailTemplateModal;
